import React, { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

gsap.registerPlugin(ScrollTrigger);

const Portfolio = () => {
  const [selectedType, setSelectedType] = useState('all');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const projectsRef = useRef([]);

  useEffect(() => {
    // Animate projects on filter change
    projectsRef.current.forEach((project, index) => {
      if (project) {
        gsap.fromTo(project,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            delay: index * 0.1,
            ease: 'power3.out'
          }
        );
      }
    });
  }, [selectedType, selectedIndustry]);

  const packagingTypes = [
    'all',
    'Boxes',
    'Corrugated Packaging',
    'Mailer Boxes',
    'Paper Bags',
    'Labels and Tags'
  ];

  const industries = [
    'all',
    'Retail',
    'E-commerce',
    'Food and Beverage',
    'Cosmetics',
    'Logistics',
    'Manufacturing'
  ];

  const projects = [
    {
      id: 1,
      title: 'Premium Retail Box',
      type: 'Boxes',
      industry: 'Retail',
      material: 'Rigid Board',
      description: 'Custom designed retail packaging with magnetic closure and premium finish',
      image: 'https://images.unsplash.com/photo-1716193348749-0be58bc06ad8'
    },
    {
      id: 2,
      title: 'E-commerce Mailer',
      type: 'Mailer Boxes',
      industry: 'E-commerce',
      material: 'Corrugated Board',
      description: 'Branded shipping boxes optimized for product protection and unboxing experience',
      image: 'https://images.unsplash.com/photo-1626253934161-08cfea22e968'
    },
    {
      id: 3,
      title: 'Food Packaging Solution',
      type: 'Boxes',
      industry: 'Food and Beverage',
      material: 'Food-Grade Board',
      description: 'Safe and attractive packaging for food products with custom printing',
      image: 'https://images.unsplash.com/photo-1633533452148-a9657d2c9a5f'
    },
    {
      id: 4,
      title: 'Cosmetic Product Box',
      type: 'Boxes',
      industry: 'Cosmetics',
      material: 'Art Card',
      description: 'Elegant packaging designed to enhance product presentation',
      image: 'https://images.unsplash.com/photo-1716540103530-cc33cdd20cde'
    },
    {
      id: 5,
      title: 'Sustainable Paper Bag',
      type: 'Paper Bags',
      industry: 'Retail',
      material: 'Kraft Paper',
      description: 'Eco-friendly shopping bags with reinforced handles',
      image: 'https://images.unsplash.com/photo-1755606277170-4e89d5847e14'
    },
    {
      id: 6,
      title: 'Industrial Corrugated Box',
      type: 'Corrugated Packaging',
      industry: 'Manufacturing',
      material: '5-Ply Corrugated',
      description: 'Heavy-duty packaging for industrial products and shipping',
      image: 'https://images.pexels.com/photos/5025503/pexels-photo-5025503.jpeg'
    },
    {
      id: 7,
      title: 'Custom Product Labels',
      type: 'Labels and Tags',
      industry: 'Cosmetics',
      material: 'Vinyl',
      description: 'High-quality labels with UV coating and custom designs',
      image: 'https://images.unsplash.com/photo-1661347232532-9376b989b51c'
    },
    {
      id: 8,
      title: 'Logistics Shipping Box',
      type: 'Corrugated Packaging',
      industry: 'Logistics',
      material: '3-Ply Corrugated',
      description: 'Standard shipping boxes with company branding',
      image: 'https://images.unsplash.com/photo-1755606396356-bdd7cd95df75'
    },
    {
      id: 9,
      title: 'Luxury Gift Box',
      type: 'Boxes',
      industry: 'Retail',
      material: 'Rigid Board with Foam',
      description: 'Premium gift packaging with custom interior layout',
      image: 'https://images.unsplash.com/photo-1700165644892-3dd6b67b25bc'
    },
    {
      id: 10,
      title: 'Beverage Carrier',
      type: 'Boxes',
      industry: 'Food and Beverage',
      material: 'Coated Board',
      description: 'Custom bottle carrier with handle and protective inserts',
      image: 'https://images.pexels.com/photos/8066787/pexels-photo-8066787.png'
    },
    {
      id: 11,
      title: 'E-commerce Mailer Box',
      type: 'Mailer Boxes',
      industry: 'E-commerce',
      material: 'Corrugated with Print',
      description: 'Branded mailer boxes with custom interior printing',
      image: 'https://images.unsplash.com/photo-1716193348749-0be58bc06ad8'
    },
    {
      id: 12,
      title: 'Retail Hang Tags',
      type: 'Labels and Tags',
      industry: 'Retail',
      material: 'Art Card',
      description: 'Custom hang tags with foil stamping and embossing',
      image: 'https://images.unsplash.com/photo-1755606464519-7513ca11a815'
    }
  ];

  const filteredProjects = projects.filter(project => {
    const typeMatch = selectedType === 'all' || project.type === selectedType;
    const industryMatch = selectedIndustry === 'all' || project.industry === selectedIndustry;
    return typeMatch && industryMatch;
  });

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Our Work</p>
            <h1 className="text-5xl lg:text-6xl font-bold text-navy-900 leading-tight">
              Packaging
              <br />
              <span className="text-gray-600">Projects</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Explore a curated selection of packaging solutions delivered across industries. 
              Each project reflects our commitment to functionality, quality, and brand presentation.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="container-custom">
          {/* Packaging Type Filter */}
          <div className="mb-8">
            <p className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
              Packaging Type
            </p>
            <div className="flex flex-wrap gap-3">
              {packagingTypes.map((type) => (
                <Button
                  key={type}
                  variant={selectedType === type ? 'default' : 'outline'}
                  onClick={() => setSelectedType(type)}
                  className={
                    selectedType === type
                      ? 'bg-navy-900 hover:bg-navy-800 text-white'
                      : 'border-gray-300 text-gray-700 hover:border-navy-900'
                  }
                >
                  {type === 'all' ? 'All Types' : type}
                </Button>
              ))}
            </div>
          </div>

          {/* Industry Filter */}
          <div>
            <p className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
              Industry
            </p>
            <div className="flex flex-wrap gap-3">
              {industries.map((industry) => (
                <Button
                  key={industry}
                  variant={selectedIndustry === industry ? 'default' : 'outline'}
                  onClick={() => setSelectedIndustry(industry)}
                  className={
                    selectedIndustry === industry
                      ? 'bg-navy-900 hover:bg-navy-800 text-white'
                      : 'border-gray-300 text-gray-700 hover:border-navy-900'
                  }
                >
                  {industry === 'all' ? 'All Industries' : industry}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <div className="mb-6">
            <p className="text-gray-600">
              Showing <strong>{filteredProjects.length}</strong> project{filteredProjects.length !== 1 ? 's' : ''}
            </p>
          </div>

          {filteredProjects.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-500">No projects found matching your filters</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <Card
                  key={project.id}
                  ref={el => projectsRef.current[index] = el}
                  className="group overflow-hidden border-2 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/50 to-transparent opacity-60"></div>
                    <div className="absolute top-4 right-4">
                      <span className="bg-white text-navy-900 px-3 py-1 rounded-full text-xs font-semibold">
                        {project.type}
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-6 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{project.industry}</span>
                      <span className="text-sm text-gray-500">{project.material}</span>
                    </div>
                    <h3 className="text-xl font-semibold text-navy-900 group-hover:text-gray-700 transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {project.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-navy-900 text-white">
        <div className="container-custom text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Create Your Packaging?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can bring your packaging vision to life
          </p>
          <Button 
            size="lg" 
            className="bg-white text-navy-900 hover:bg-gray-100 text-lg px-8 py-6"
            asChild
          >
            <a href="/contact">Start a Project</a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
